import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Button, TablePagination, Pagination, PaginationItem } from '@mui/material';
import medal_gold from '../../images/gamefacts/medal/medal_gold.png'
import medal_silver from '../../images/gamefacts/medal/medal_silver.png'
import medal_bronze from '../../images/gamefacts/medal/medal_bronze.png'

import './classementDetail.css'
import { useLiveResolver } from '../../hooks/useLiveResolver.js';

import nationalities from '../../BDD/nationalities.js'
import SearchFilterDTO from '../../DTO/SearchFilterDTO.js';
import { getCountryFlag } from '../utils/flags.js';
import { getV2LiveById } from '../../../api/index.js';
import LiveDTO from '../../DTO/LiveDTO.js';
import { getImage } from '../utils/images.js';



const ClassementDetail = () => {

  const [queryParams] = useSearchParams();
  const displayLimit = queryParams.get('limit') || undefined;

  const { dataComponent, pager, setPage, setLimit } = useOutletContext();

  console.log('pagination', dataComponent?.pagination);

    if(!dataComponent)
        return <CircularProgress />

    // const {component} = dataComponent

    return (
      <DataComponent
        dataComponent={dataComponent}
        displayLimit={displayLimit}
        pager={pager}
        setPage={setPage}
        setLimit={setLimit}
      />
    );

};

const DataComponent = ({ dataComponent, displayLimit, pager, setPage, setLimit }) => {

  const {live, competitionDetail, viewName, component, columns, data, focus} = dataComponent

  const resolveComponent = (type) => {

    switch(type){
      case 'Table':
        return (
          <TableComponent
            component={component}
            columns={columns}
            data={data}
            focus={focus}
            displayLimit={displayLimit}
            pagination={dataComponent.pagination} // undefined si back non paginé
            pager={pager}
            setPage={setPage}
            setLimit={setLimit}
          />
        );
      default : 
        return <CircularProgress />
    }
  }

  if(!data || !data.length)
    return <Typography  pt={"10px"}>Les informations seront affichées dès que les premiers résultats officiels seront communiqués.</Typography>
  
  return <Box sx={{display : 'flex', alignItems : 'center', flexDirection : 'column'}}>
            {resolveComponent(dataComponent.component.type)}
        </Box>
        

}

function TablePaginationActions(props) {
  const { count, page, rowsPerPage, onPageChange } = props;
  const pageCount = rowsPerPage > 0 ? Math.ceil(count / rowsPerPage) : 0;

  return (
    <Pagination
      count={pageCount}
      page={page + 1} // Pagination = 1-based
      onChange={(e, value) => onPageChange(e, value - 1)} // TablePagination = 0-based
      siblingCount={1}
      boundaryCount={1}
      showFirstButton={false}
      showLastButton={false}
      renderItem={(item) => <PaginationItem {...item} />}
    />
  );
}

const TableComponent = ({
  component,
  columns,
  data,
  focus = [],
  displayLimit,
  pagination,   // <-- server meta (optionnel)
  pager,        // <-- { page, limit } côté resolver (optionnel)
  setPage,      // <-- setter resolver (optionnel)
  setLimit      // <-- setter resolver (optionnel)
}) => {
  const { lineVisible } = component;

  // si displayLimit est fixé (mode embed), on garde un comportement simple (pas de pagination)
  const isLimited = displayLimit !== undefined && displayLimit !== null && displayLimit !== '';

  // mode server si pagination existe + handlers
  const isServer = !!pagination && typeof setPage === 'function' && typeof setLimit === 'function';

  // --- pagination locale (fallback)
  const defaultRowsPerPage = isLimited
    ? Number(displayLimit)
    : (Number(lineVisible) || 10);

  const [localPage, setLocalPage] = useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = useState(defaultRowsPerPage);

  // reset pagination locale quand les données changent
  useEffect(() => {
    if (isServer) return; // en server-side, c'est le resolver qui pilote
    setLocalPage(0);
    if (!isLimited) setLocalRowsPerPage(Number(lineVisible) || 10);
  }, [data, lineVisible, isLimited, isServer]);

  const resolveColumnTitle = (column) => {
    const { title } = column;
    if (!title) return;
    const { name, logo, alt } = title;
    if (name) return name;
    if (logo) return getImage(logo, alt);
  };

  const resolveCell = (entry, key) => {
    const column = columns.find(c => c.id === key);
    const value = entry[key];
    if (!column) return '';

    let content = '';
    switch (column.format) {
      case 'Flag':
        content = getCountryFlag(value);
        break;
      default:
        content = value;
    }

    return (
      <TableCell key={key} sx={{ ...entry.style, ...column.style }}>
        {content}
      </TableCell>
    );
  };

  const orderedRowKeys = useCallback((obj) => {
    return Object.keys(obj).sort((a, b) => {
      const columnA = columns.find(c => c.id === a);
      const columnB = columns.find(c => c.id === b);
      if (!columnA || !columnB) return 0;
      return columnA.order - columnB.order;
    });
  }, [columns]);

  const pagedItems = useMemo(() => {
    if (!data) return [];
    if (isLimited) return data.slice(0, Number(displayLimit));

    if (isServer) {
      // back a déjà paginé data
      return data;
    }

    // fallback local
    const start = localPage * localRowsPerPage;
    const end = start + localRowsPerPage;
    return data.slice(start, end);
  }, [data, isLimited, displayLimit, isServer, localPage, localRowsPerPage]);

  // Valeurs MUI
  const muiCount = isServer ? (pagination.total ?? 0) : (data?.length || 0);
  const muiPage = isServer ? Math.max(0, (pagination.page ?? 1) - 1) : localPage; // server: 1-based
  const muiRowsPerPage = isServer ? (pagination.limit ?? defaultRowsPerPage) : localRowsPerPage;

  return (
    <React.Fragment>
      <TableContainer>
        <Table>
          <TableHead>
            {focus && focus.map((f, idx) => (
              <TableRow
                className="focusRank"
                key={"focus_" + idx}
                sx={{
                  p: 0,
                  borderLeft: '1px solid black',
                  borderRight: '1px solid black',
                  borderTop: idx === 0 ? '1px solid black' : '',
                  borderBottom: idx === focus.length - 1 ? '1px solid black' : ''
                }}
              >
                {orderedRowKeys(f).map(key => resolveCell(f, key))}
              </TableRow>
            ))}

            {focus.length > 0 && <TableRow sx={{ height: '1em' }} />}

            <TableRow sx={{ pt: 3 }}>
              {columns?.sort((a, b) => a.order - b.order)
                .map(column => (
                  <TableCell key={'head_' + column.id} sx={{ ...column.style }}>
                    {resolveColumnTitle(column)}
                  </TableCell>
                ))
              }
            </TableRow>
          </TableHead>

          <TableBody>
            {pagedItems.map((entry, idx) => (
              <TableRow
                key={'row_' + (isServer ? idx : (localPage * localRowsPerPage + idx))}
                sx={{ p: 0 }}
              >
                {orderedRowKeys(entry).map(key => resolveCell(entry, key))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination (désactivée si displayLimit est forcé) */}
      {!isLimited && (
        <Box sx={{ mt: 2 }}>
        <TablePagination
          component="div"
          count={muiCount}
          page={muiPage}
          rowsPerPage={muiRowsPerPage}
          onPageChange={(_, newPage) => {
            if (isServer) setPage(newPage + 1);
            else setLocalPage(newPage);
          }}
          onRowsPerPageChange={(e) => {
            const next = parseInt(e.target.value, 10);
            if (isServer) {
              setLimit(next);
              setPage(1);
            } else {
              setLocalRowsPerPage(next);
              setLocalPage(0);
            }
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage=""                 // on masque le label à gauche
          labelDisplayedRows={() => ""}       // on masque "1–10 sur 123"
          ActionsComponent={TablePaginationActions}
          SelectProps={{
            renderValue: (v) => `${v} / Page`, // "10 / Page"
            variant: "standard",
            disableUnderline: true,
          }}
          sx={{
            // conteneur global
            // borderTop: "1px solid",
            borderColor: "divider",
            py: 0.75,

            // toolbar: pagination à gauche, select à droite
            "& .MuiTablePagination-toolbar": {
              minHeight: "auto",
              px: 1,
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
            },

            // cache bien toutes les zones texte
            "& .MuiTablePagination-displayedRows": { display: "none" },
            "& .MuiTablePagination-selectLabel": { display: "none" },

            // on veut les "actions" (Pagination) à gauche, sans marge auto
            "& .MuiTablePagination-actions": {
              marginLeft: 0,
            },

            // style "pill" des numéros + chevrons
            "& .MuiPaginationItem-root": {
              borderRadius: "999px",
              minWidth: 28,
              height: 28,
              mx: 0.25,
            },
            "& .MuiPagination-ul": {
              alignItems: "center",
            },

            // style du select à droite (petit "chip" comme sur ta capture)
            "& .MuiTablePagination-select": {
              px: 1,
              py: 0.25,
              borderRadius: "999px",
              border: "1px solid",
              borderColor: "divider",
              fontSize: 13,
            },
            "& .MuiTablePagination-input": {
              marginRight: 0,
            },

            // virer l'espace réservé à droite si besoin
            "& .MuiTablePagination-spacer": { display: "none" },
          }}
        />
        </Box>
      )}
    </React.Fragment>
  );
};

const MedalTable = ({ classement }) => {

    const [sortedClassement, setSortedClassement] = useState([])

    const [toggled, setToggled] = useState(false);
    const defaultVisibleItems = 10

    const [franceEntry, setFranceEntry] = useState(null)

    useEffect(() => {

      let rank = 1; // Le rang initial
      let previousGold = null;
      let previousSilver = null;
      let previousBronze = null;
      let previousRank = 1;

      const sortClassement = classement?.sort((a, b) => {
        const goldA = getMedalCount(a.gameFacts, 'gold');
        const goldB = getMedalCount(b.gameFacts, 'gold');
        if (goldA !== goldB) return goldB - goldA;
      
        const silverA = getMedalCount(a.gameFacts, 'silver');
        const silverB = getMedalCount(b.gameFacts, 'silver');
        if (silverA !== silverB) return silverB - silverA;
      
        const bronzeA = getMedalCount(a.gameFacts, 'bronze');
        const bronzeB = getMedalCount(b.gameFacts, 'bronze');
        return bronzeB - bronzeA;
      }).map((entry, idx) => {
        const gold = getMedalCount(entry.gameFacts, 'gold');
        const silver = getMedalCount(entry.gameFacts, 'silver');
        const bronze = getMedalCount(entry.gameFacts, 'bronze');
      
        if (idx > 0 && gold === previousGold && silver === previousSilver && bronze === previousBronze) {
          rank = previousRank;
        } else {
          if (idx > 0) {
            rank = idx + 1
            previousRank = rank;
          };
        }
      
        previousGold = gold;
        previousSilver = silver;
        previousBronze = bronze;
        
        return {...entry, rank};
      });

      const emptyNationalities = nationalities.sort((a, b) => a.localeCompare(b)).filter(n => !sortClassement.find(t => t._id === n)).map(n => ({
        gameFacts : [], label : n, total : 0, _id : n, rank : '-'
      }))

      const fullClassement = sortClassement.concat(emptyNationalities)
      setFranceEntry(fullClassement.find(c => c._id === 'France'))

    setSortedClassement(fullClassement)

    }, [classement])

    const visibleItems = useMemo(() => {

      if(!sortedClassement)
        return []

      if(toggled)
        return sortedClassement

      return sortedClassement.slice(0, defaultVisibleItems)

    }, [sortedClassement, toggled])
  

   if (!sortedClassement || !sortedClassement.length) {
      return <CircularProgress/>
    }

    return (
      <React.Fragment>
        {
      classement && (<TableContainer>
        <Table aria-label="medal table">
          <TableHead>
          {franceEntry && 
              <TableRow
                className='focusRank'
                key={franceEntry.label}
                sx={{ p : 0, border : '1px solid black'}}
              >
                <TableCell sx={{width : 20 }}>{franceEntry.rank}</TableCell>
                <TableCell sx={{width : 20 }}>{getCountryFlag(franceEntry.label)}</TableCell>
                <TableCell sx={{fontWeight : 'bold' }}>{franceEntry.label}</TableCell>
                <TableCell align="center">{getMedalCount(franceEntry.gameFacts, 'gold')}</TableCell>
                <TableCell align="center">{getMedalCount(franceEntry.gameFacts, 'silver')}</TableCell>
                <TableCell align="center">{getMedalCount(franceEntry.gameFacts, 'bronze')}</TableCell>
                <TableCell align="center">{getTotalMedals(franceEntry.gameFacts)}</TableCell>
              </TableRow>
            }
            <TableRow
                sx={{ height : '1em'}}
              >
              </TableRow>
            <TableRow sx={{pt : 3}}>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              {
                //getImage(row.title.logo)
              }
              <TableCell align="center"><img src={medal_gold}></img></TableCell>
              <TableCell align="center"><img src={medal_silver}></img></TableCell>
              <TableCell align="center"><img src={medal_bronze}></img></TableCell>
              <TableCell align="center">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleItems?.map((entry) => (
              <TableRow
                key={entry.label}
                sx={{ p : 0}}
              >
                <TableCell component="th" scope="row">{entry.rank}</TableCell>
                <TableCell>{getCountryFlag(entry.label)}</TableCell>
                <TableCell sx={{fontWeight : entry._id === 'France' ? 'bold' : 'normal' }}>{entry.label}</TableCell>
                <TableCell align="center">{getMedalCount(entry.gameFacts, 'gold') || '-'}</TableCell>
                <TableCell align="center">{getMedalCount(entry.gameFacts, 'silver') || '-'}</TableCell>
                <TableCell align="center">{getMedalCount(entry.gameFacts, 'bronze') || '-'}</TableCell>
                <TableCell align="center">{getTotalMedals(entry.gameFacts)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>)
      }
          {(visibleItems.length < sortedClassement.length || toggled) && <Button sx={{width : 100, mt : 5, bgcolor : "multiplex.darkblue", fontSize : '0.7em'}} onClick={() => setToggled(!toggled ? true : false)} variant="contained">
            {!toggled ? 'Voir plus' : 'Voir moins'}
          </Button>}
</React.Fragment>
    );
};
  
  // Fonction pour récupérer le nombre de médailles par type
  const getMedalCount = (gameFacts, medalType) => {
    const fact = gameFacts.find(fact => fact.value === medalType);
    return fact ? fact.count : 0;
  };
  
  // Fonction pour calculer le total de médailles
  const getTotalMedals = (gameFacts) => {
    return gameFacts.reduce((acc, fact) => acc + fact.count, 0);
  };



export default ClassementDetail